// uploadUtils.js
import axios from 'axios';
import { ElMessage } from 'element-plus';
import config from '@/config.js';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';

// 允许的图片类型 (bmp/png/jpeg/jpg/gif)
const ALLOWED_IMAGE_TYPES = [
  'image/bmp',
  'image/png', 
  'image/jpeg', 
  'image/gif'
];

// 允许的视频类型 (mp4)
const ALLOWED_VIDEO_TYPES = [
  'video/mp4'
];

// 允许的音频类型 (mp3/wma/wav/amr)
const ALLOWED_AUDIO_TYPES = [
  'audio/mpeg', // mp3
  'audio/x-ms-wma', // wma
  'audio/wav', // wav
  'audio/amr' // amr
];

// 最大文件大小限制 (单位: 字节)
const MAX_FILE_SIZES = {
  image: 2 * 1024 * 1024, // 2MB
  video: 10 * 1024 * 1024, // 10MB
  audio: 2 * 1024 * 1024 // 2MB
};

/**
 * 获取上传签名
 * @param {string} type 文件类型（如 'video', 'image', 'audio', 'document' 等）
 * @returns {Promise<Object>} 返回签名数据
 */
export const getUploadSignature = async (type = 'file') => {
  try {
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}');
    // 验证必要字段存在
    if (!userInfo.id) {
      throw new Error('用户信息中缺少商户ID');
    }
    const response = await axios.post(
      `${config.BASE_URL}/shop/api/upload_signature.php`,
      {
        type, // 上传类型
        id: userInfo.id // 使用代理ID作为店铺标识
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // 检查接口返回的数据
    if (response.data && response.data.success) {
      // 确保返回的数据包含所有必需的字段
      const requiredFields = ['accessKeyId', 'policy', 'signature', 'bucket', 'endpoint', 'folder'];
      for (const field of requiredFields) {
        if (!response.data[field]) {
          throw new Error(`接口返回数据缺少字段: ${field}`);
        }
      }

      // 返回签名数据
      return response.data;
    } else {
      // 如果接口返回 success 为 false，抛出错误
      throw new Error(response.data.error || "获取上传签名失败");
    }
  } catch (error) {
    console.error("获取上传签名失败:", error);
    throw error;
  }
};

/**
 * 获取文件类型分类
 * @param {string} mimeType MIME类型
 * @returns {string} 返回文件类型分类 ('image', 'video', 'audio')
 */
const getFileCategory = (mimeType) => {
  if (ALLOWED_IMAGE_TYPES.includes(mimeType)) return 'image';
  if (ALLOWED_VIDEO_TYPES.includes(mimeType)) return 'video';
  if (ALLOWED_AUDIO_TYPES.includes(mimeType)) return 'audio';
  return 'file';
};

/**
 * 上传文件到 OSS
 * @param {File} file 要上传的文件
 * @param {Object} signatureData 签名数据
 * @returns {Promise<{ success: boolean, url: string, size: number }>} 返回上传结果
 */
export const uploadToOSS = async (file, signatureData) => {
  const fileCategory = getFileCategory(file.type);
  
  // 验证文件类型
  let allowedTypes;
  switch(fileCategory) {
    case 'image':
      allowedTypes = ALLOWED_IMAGE_TYPES;
      break;
    case 'video':
      allowedTypes = ALLOWED_VIDEO_TYPES;
      break;
    case 'audio':
      allowedTypes = ALLOWED_AUDIO_TYPES;
      break;
    default:
      throw new Error(`不支持的文件类型: ${file.type}`);
  }

  if (!allowedTypes.includes(file.type)) {
    throw new Error(`不支持的文件类型: ${file.type}，仅支持 ${allowedTypes.join(', ')}`);
  }

  // 验证文件大小
  const maxSize = MAX_FILE_SIZES[fileCategory];
  if (file.size > maxSize) {
    throw new Error(`文件大小超过限制 (${maxSize / 1024 / 1024}MB): ${(file.size / 1024 / 1024).toFixed(2)}MB`);
  }

  return new Promise((resolve, reject) => {
    const uniqueFileName = `${uuidv4()}.${file.name.split('.').pop()}`; // 生成唯一的文件名

    const formData = new FormData();
    formData.append("key", `${signatureData.folder}${uniqueFileName}`); // 文件路径
    formData.append("policy", signatureData.policy); // 上传策略
    formData.append("OSSAccessKeyId", signatureData.accessKeyId); // AccessKeyId
    formData.append("signature", signatureData.signature); // 签名
    formData.append("success_action_status", "200"); // 上传成功时返回的状态码
    formData.append("file", file); // 文件

    axios.post(
      `https://${signatureData.bucket}.${signatureData.endpoint}`, // OSS 上传地址
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data", // 设置请求头
        },
      }
    )
      .then((response) => {
        if (response.status === 200) {
          // 上传成功
          const fileUrl = `https://${signatureData.bucket}.${signatureData.endpoint}/${signatureData.folder}${uniqueFileName}`;
          resolve({ 
            success: true, 
            url: fileUrl, 
            size: file.size,
            type: file.type,
            category: fileCategory
          });
        } else {
          // 上传失败
          console.error("上传失败：", response);
          reject(new Error("上传失败"));
        }
      })
      .catch((error) => {
        console.error("上传失败：", error);
        reject(error);
      });
  });
};

/**
 * 上传文件前的校验
 * @param {File} file 要上传的文件
 * @param {Object} options 校验选项
 * @param {number} options.maxSize 文件最大大小（MB）
 * @param {string} options.type 文件类型 ('image', 'video', 'audio')
 * @returns {Promise<boolean>} 返回校验结果
 */
export const beforeUpload = async (file, options = {}) => {
  const { maxSize, type } = options;
  const fileCategory = type || getFileCategory(file.type);
  
  // 确定允许的文件类型
  let allowedTypes;
  switch(fileCategory) {
    case 'image':
      allowedTypes = ALLOWED_IMAGE_TYPES;
      break;
    case 'video':
      allowedTypes = ALLOWED_VIDEO_TYPES;
      break;
    case 'audio':
      allowedTypes = ALLOWED_AUDIO_TYPES;
      break;
    default:
      ElMessage.error(`不支持的文件类型: ${file.type}`);
      return false;
  }

  // 校验文件类型
  if (!allowedTypes.includes(file.type)) {
    ElMessage.error(`不支持的文件类型: ${file.type}，仅支持 ${allowedTypes.join(', ')}`);
    return false;
  }

  // 校验文件大小
  const maxAllowedSize = maxSize 
    ? Math.min(maxSize * 1024 * 1024, MAX_FILE_SIZES[fileCategory])
    : MAX_FILE_SIZES[fileCategory];
    
  if (file.size > maxAllowedSize) {
    ElMessage.error(`文件大小不能超过 ${(maxAllowedSize / 1024 / 1024).toFixed(2)}MB`);
    return false;
  }

  return true;
};

/**
 * 获取视频/音频时长
 * @param {File} file 视频/音频文件
 * @returns {Promise<number>} 返回视频/音频时长（秒）
 */
export const getVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    const media = document.createElement(
      file.type.startsWith('video/') ? 'video' : 
      file.type.startsWith('audio/') ? 'audio' : 
      'video'
    );
    media.src = URL.createObjectURL(file);
    media.onloadedmetadata = () => {
      resolve(media.duration);
      URL.revokeObjectURL(media.src); // 释放内存
    };
    media.onerror = () => {
      URL.revokeObjectURL(media.src); // 释放内存
      reject(new Error("无法读取文件时长"));
    };
  });
};

/**
 * 获取文件缩略图（适用于图片和视频）
 * @param {File} file 文件
 * @returns {Promise<string>} 返回缩略图URL
 */
export const getFileThumbnail = (file) => {
  return new Promise((resolve, reject) => {
    if (file.type.startsWith('image/')) {
      // 图片文件直接生成缩略图
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    } else if (file.type.startsWith('video/')) {
      // 视频文件获取第一帧作为缩略图
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      video.src = URL.createObjectURL(file);
      
      video.addEventListener('loadedmetadata', () => {
        video.currentTime = Math.min(1, video.duration);
      });
      
      video.addEventListener('seeked', () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg'));
        URL.revokeObjectURL(video.src);
      });
      
      video.addEventListener('error', () => {
        URL.revokeObjectURL(video.src);
        reject(new Error("无法生成视频缩略图"));
      });
    } else {
      reject(new Error("不支持的文件类型"));
    }
  });
};